import { Link, useParams } from 'react-router-dom'
import { TrendingUp, Edit, FileText, Calendar, MapPin, Phone, Package } from 'lucide-react'
import { useDeviceAsset } from '@/hooks/asset'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { DEVICE_STATUS_LABELS, type DeviceAssetStatus } from '@/api/types/asset'

type DeviceStatusDisplay = DeviceAssetStatus | 'UNKNOWN'

function DeviceDetail() {
  const { id } = useParams()
  const deviceId = id ? parseInt(id, 10) : 0

  const { data: device, isLoading, error } = useDeviceAsset(deviceId)

  const statusConfig: Record<DeviceStatusDisplay, { label: string; class: string }> = {
    ACTIVE: { label: DEVICE_STATUS_LABELS.ACTIVE, class: 'bg-green-100 text-green-600' },
    MAINTENANCE: { label: DEVICE_STATUS_LABELS.MAINTENANCE, class: 'bg-red-100 text-red-600' },
    RETIRED: { label: DEVICE_STATUS_LABELS.RETIRED, class: 'bg-gray-100 text-gray-600' },
    DISPOSED: { label: DEVICE_STATUS_LABELS.DISPOSED, class: 'bg-gray-200 text-gray-500' },
    UNKNOWN: { label: '未设置', class: 'bg-gray-100 text-gray-500' },
  }

  const maintenanceRecords = [
    { date: '2026-03-20', type: '预防性保养', engineer: '张继伟', status: '已完成', result: '正常' },
    { date: '2025-12-15', type: '预防性保养', engineer: '李明', status: '已完成', result: '正常' },
    { date: '2025-09-10', type: '故障维修', engineer: '王工', status: '已完成', result: '更换配件' },
    { date: '2025-06-05', type: '预防性保养', engineer: '张继伟', status: '已完成', result: '正常' },
  ]

  const packages = [
    { id: '1', type: 'MANUAL', name: '操作手册.pdf', size: '2.3 MB', date: '2023-03-15' },
    { id: '2', type: 'DRIVER', name: '系统驱动_v2.1.exe', size: '156 MB', date: '2023-05-20' },
    { id: '3', type: 'CONFIG', name: '设备配置文件.json', size: '12 KB', date: '2023-03-20' },
  ]

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载设备详情..." />
      </div>
    )
  }

  if (error || !device) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  const statusInfo = statusConfig[device.status ?? 'UNKNOWN']

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supply-chain/device/list" className="p-2 hover:bg-gray-100 rounded-lg transition">
              <TrendingUp className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{device.deviceName}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {device.manufacturer} {device.deviceModel} · {device.deviceSerialNo}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2">
              <FileText className="w-4 h-4" />
              打印档案
            </button>
            <button className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition flex items-center gap-2">
              <Edit className="w-4 h-4" />
              编辑信息
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">基本信息</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">设备名称</span>
                    <span className="font-medium text-gray-900">{device.deviceName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">资产编码</span>
                    <span className="font-medium text-gray-900 font-mono">{device.assetCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">制造商</span>
                    <span className="font-medium text-gray-900">{device.manufacturer}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">设备型号</span>
                    <span className="font-medium text-gray-900">{device.deviceModel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">序列号</span>
                    <span className="font-medium text-gray-900 font-mono">{device.deviceSerialNo}</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">所属医院</span>
                    <span className="font-medium text-gray-900">{device.hospitalName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">科室</span>
                    <span className="font-medium text-gray-900">{device.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">安装位置</span>
                    <span className="font-medium text-gray-900 text-right">{device.installLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">创建时间</span>
                    <span className="font-medium text-gray-900">
                      {new Date(device.createdAt).toLocaleDateString('zh-CN')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">更新时间</span>
                    <span className="font-medium text-gray-900">
                      {new Date(device.updatedAt).toLocaleDateString('zh-CN')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">保修与采购</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">制造商</span>
                    <span className="font-medium text-gray-900">{device.manufacturer}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">客户名称</span>
                    <span className="font-medium text-gray-900">{device.customerName}</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">资产状态</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.class}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">保养维修记录</h2>
              <Link to="#" className="text-primary-600 text-sm font-medium hover:underline">查看全部</Link>
            </div>
            <div className="p-5">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                    <th className="pb-3 font-medium">日期</th>
                    <th className="pb-3 font-medium">类型</th>
                    <th className="pb-3 font-medium">工程师</th>
                    <th className="pb-3 font-medium">状态</th>
                    <th className="pb-3 font-medium">结果</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {maintenanceRecords.map((record, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-3 text-gray-600">{record.date}</td>
                      <td className="py-3 text-gray-900">{record.type}</td>
                      <td className="py-3 text-gray-600">{record.engineer}</td>
                      <td className="py-3">
                        <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs">{record.status}</span>
                      </td>
                      <td className="py-3 text-gray-600">{record.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">设备资料</h2>
              <button className="text-primary-600 text-sm font-medium flex items-center gap-1 hover:underline">
                <Package className="w-4 h-4" />
                上传资料
              </button>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                        📄
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{pkg.name}</div>
                        <div className="text-xs text-gray-500">{pkg.size} · {pkg.date}</div>
                      </div>
                    </div>
                    <button className="text-primary-600 text-sm hover:underline">下载</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">设备状态</h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">资产状态</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.class}`}>
                  {statusInfo.label}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">资产编码</span>
                <span className="font-mono text-sm text-gray-900">{device.assetCode}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">联系方式</h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">安装位置</div>
                  <div className="font-medium text-gray-900">{device.installLocation}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">医院/科室</div>
                  <div className="font-medium text-gray-900">
                    {device.hospitalName} / {device.department}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">快捷操作</h2>
            </div>
            <div className="p-5 space-y-2">
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <Calendar className="w-5 h-5 text-gray-400" />
                创建保养计划
              </button>
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <FileText className="w-5 h-5 text-gray-400" />
                报障维修
              </button>
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <Package className="w-5 h-5 text-gray-400" />
                查看关联工单
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeviceDetail
